import type { Module } from 'vuex';
import { IFilter, IQueryFilter } from '../../entities/filter-entity';
import {FilterPage} from "sitisjs/dist/internal/sections/categories/models/FilterPage";

export const namespace = 'categoryFilters';

interface ILoadingState {
	get: boolean;
	update: boolean;
}

export interface IGetFiltersParams {
	slug: string;
	slugs: string[];
	filters: any,
	canceledOptions?: any,

	mixin?: string,
	isHideLoad?: boolean
}
export interface IGetFilterPageParams {
	path: String,

	mixin?: string
}

export interface IUpdateFiltersParams extends IGetFiltersParams {
	filters: IQueryFilter[];
	mixin?: string;
}

export interface ICategoryFiltersState {
	filters: IFilter[];
	loading: ILoadingState;
	filterPages: any[];
	filterPrices: any;
	filterInfo: any;
}

export const MutationType = {
	SET_LOADING: 'setLoading',
	SET_FILTERS: 'setFilters',
	SET_FILTER_PAGE: 'setFilterPage',
	UPDATE_FILTERS: 'updateFilters',
	SET_FILTER_PRICES: 'updatePrices',
	SET_FILTER_INFO: 'setFilterInfo',
};

export const actionType = {
	GET_FILTERS: 'getFilters',
	GET_FILTER_PAGES: 'getFilterPages',
	UPDATE_FILTERS: 'updateFilters',
};

const actionFiltersBySlug: any = {
	"category": "getFiltersByCategorySlug",
	"promotions": "getFiltersByPromotionSlug",
	"brands": "getFiltersByBrandSlug",
	"sets": "getFiltersBySetSlug",
};
const actionAvailableFilterVariants: any = {
	"category": "getAvailableFilterVariants",
	"promotions": "getAvailableFilterVariantsByPromotion",
	"brands": "getAvailableFilterVariantsByBrand",
	"sets": "getAvailableFilterVariantsBySet",
};
const executeNameSlugs: any = {
	"category": "categorySlug",
	"promotions": "promotionSlug",
	"brands": "brandSlug",
	"sets": "setSlug",
};

export default function catalogFiltersStore(): Module<ICategoryFiltersState, any> {
	return {
		namespaced: true,
		state: () => ({
			filterPages: [],
			filters: [],
			filterInfo: {},
			loading: {
				get: true,
				update: false,
			},
			filterPrices: {},
		}),
		actions: {
			async [actionType.GET_FILTERS]({ commit }, { slug, slugs, filters, mixin, isHideLoad, canceledOptions }: IGetFiltersParams): Promise<IFilter[]> {
				try {
					/** NOTE FIX
					 * sitisjs/src/internal/sections/categories/models/Filter.ts
					 * Добавить slug: string;
					 */
					commit('appState/setLoading', { filters: false }, { root: true });
					if (!isHideLoad) {
						commit(MutationType.SET_LOADING, { key: 'get', value: true });
					}

					const methodFilter = actionFiltersBySlug[mixin || "category"];
					const executeNameSlug = executeNameSlugs[mixin || "category"];

					const res = await this.$api.filter?.[methodFilter].execute({
						[executeNameSlug]: slug,
						slugs,
						filters,
						expanded: true,
						canceledOptions: canceledOptions || {}
					});
					if (res.isSuccess) {
						const value = res.getValue();

						if (value) {
							commit(MutationType.SET_FILTERS, value.filters);
							// commit(MutationType.SET_FILTER_PAGE, value.filterPages);
							commit(MutationType.SET_FILTER_PRICES, value.prices);

							return value.filters;
						}
					}
					return [];
				} catch (error) {
					// eslint-disable-next-line no-console
					console.log('@onGetFilters error: ', error);
					return [];
				} finally {
					commit('appState/setLoading', { filters: false }, { root: true });
					commit(MutationType.SET_LOADING, { key: 'get', value: false });
				}
			},
			async [actionType.GET_FILTER_PAGES]({ commit }, { path, mixin }: IGetFilterPageParams): Promise<any> {
				try {
					commit('appState/setLoading', { filters: true }, { root: true });
					const response = await this.$api.agent.transport.get(`/filter/info?path=${ path }`).then((res: any) => {
						return res.data
					}).catch((err: any) => {
						return {error: true}
					})
					if (response?.error) {
						return response
					}
					if (response?.redirect) {
						return response
					}

					// Filter Pages
					const filterPages = (response.filterPages || []).map((ft: any) => {
						return new FilterPage(ft);
					})
					commit(MutationType.SET_FILTER_PAGE, filterPages);
					commit(MutationType.SET_FILTER_INFO, response);

					return filterPages;
				} catch (error) {
					// eslint-disable-next-line no-console
					console.log('@onGetFilters error: ', error);
					return [];
				} finally {
					commit('appState/setLoading', { filters: false }, { root: true });
				}
			},
			async [actionType.UPDATE_FILTERS](
				{ commit, state },
				{ filters, slug, slugs, mixin }: IUpdateFiltersParams,
			): Promise<Record<string, number[]> | undefined> {
				try {
					/** NOTE FIX реализовал метод обновления фильтров */
					commit('appState/setLoading', { updateFilters: true }, { root: true });

					const methodAvailableFilter = actionAvailableFilterVariants[mixin || "category"];
					const executeNameSlug = executeNameSlugs[mixin || "category"];
					const res = await this.$api.filter?.[methodAvailableFilter].execute({
						[executeNameSlug]: slug,
						filters,
						slugs,
					});
					if (res.isSuccess) {
						const value = res.getValue();
						if (value) {
							const updatedFilters: IFilter[] = [];
							state.filters.forEach((filter) => {
								const availableFilter = value.availableFilterVariants[filter.id];
								if (availableFilter) {
									updatedFilters.push({
										...filter,
										available: availableFilter,
									});
								} else {
									updatedFilters.push({
										...filter
									});
								}
							});

							commit(MutationType.SET_FILTERS, updatedFilters);
							commit(MutationType.SET_FILTER_PAGE, value.filterPages);

							return value.availableFilterVariants;
						}
					}
					return {};
				} catch (error) {
					// eslint-disable-next-line no-console
					console.log('@onGetFilters error: ', error);
					return undefined;
				} finally {
					commit('appState/setLoading', { updateFilters: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_LOADING]: (state, { key, value }: { key: keyof ILoadingState; value: boolean }): void => {
				state.loading[key] = value;
			},
			[MutationType.SET_FILTERS]: (state, filters: IFilter[]): void => {
				state.filters = filters;
			},
			[MutationType.SET_FILTER_INFO]: (state, filterInfo: any): void => {
				state.filterInfo = filterInfo;
			},
			[MutationType.SET_FILTER_PAGE]: (state, filterPages: any): void => {
				state.filterPages = filterPages;
			},
			[MutationType.SET_FILTER_PRICES]: (state, filterPrices: any): void => {
				state.filterPrices = filterPrices;
			},
		},
		getters: {
			filters: ({ filters }) => filters,
			loading: ({ loading }) => loading,
			filterInfo: ({ filterInfo }) => filterInfo,
			filterPages: ({ filterPages }) => filterPages,
			filterPrices: ({ filterPrices }) => filterPrices,
		},
	};
}
